import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';

import Rail from './appShell/Rail';
import TopBar from './appShell/TopBar';
import MobileTopBar from './appShell/MobileTopBar';
import CommandPalette from './appShell/CommandPalette';
import useActiveNav from './appShell/useActiveNav';
import useEntitySearch from './appShell/useEntitySearch';
import { BREADCRUMB_EVENT, readBreadcrumbs } from './appShell/breadcrumbBridge';
import { C, RAIL_WIDTH, RAIL_WIDTH_COLLAPSED, DESKTOP_QUERY } from './appShell/tokens';

const LS_EXPANDED = 'delivery_rail_expanded';
const SEARCH_PLACEHOLDER = 'Buscar orden, guía, RMA, mensajero…';

const leafShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  target: PropTypes.string,
});

const propTypes = {
  rootURL: PropTypes.string.isRequired,
  logoURL: PropTypes.string.isRequired,
  logoIconURL: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      url: PropTypes.string,
      count: PropTypes.number,
      leaves: PropTypes.arrayOf(leafShape),
    })),
  })),
  logoutLink: PropTypes.shape({
    name: PropTypes.string, url: PropTypes.string, method: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.shape({
    name: PropTypes.string, role: PropTypes.string, initials: PropTypes.string,
  }).isRequired,
  currentPath: PropTypes.string.isRequired,
  currentFullPath: PropTypes.string,
};

const defaultProps = { sections: [], currentFullPath: null };

// The app shell: a grouped rail on the left, a header above the server-rendered page, and on a
// phone the rail behind that header's menu button. Replaces the flat nine-link top bar.
const AppShell = (props) => {
  const { sections, currentPath, currentFullPath } = props;

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const isMobile = !isDesktop;

  const active = useActiveNav(sections, currentPath, currentFullPath);

  // Expanded at every size: five sections only read as sections when their headings are
  // visible, and the width that costs the tables is the trade taken. Collapsing is a
  // preference the user keeps, so only an explicit '0' overrides the default.
  const [expanded, setExpanded] = useState(() => {
    try {
      return window.localStorage.getItem(LS_EXPANDED) !== '0';
    } catch (e) {
      return true;
    }
  });
  const [openGroupId, setOpenGroupId] = useState(active.groupId);
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Server-provided breadcrumbs published by the current page (shared/AppPage).
  const [pageTrail, setPageTrail] = useState(() => readBreadcrumbs());

  useEffect(() => {
    const onCrumbs = (e) => setPageTrail(e.detail || null);
    window.addEventListener(BREADCRUMB_EVENT, onCrumbs);
    return () => window.removeEventListener(BREADCRUMB_EVENT, onCrumbs);
  }, []);

  // Width and height the server-rendered <main> should offset by (see delivery_shell.scss).
  const railWidth = isMobile ? 0 : (expanded ? RAIL_WIDTH : RAIL_WIDTH_COLLAPSED);
  // Layout effect (pre-paint) keeps the offset in sync without a one-frame flash.
  useLayoutEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--delivery-rail-w', `${railWidth}px`);
  }, [railWidth]);

  // The drawer belongs to a viewport that no longer exists once the rail is back.
  useEffect(() => {
    if (isDesktop) setNavOpen(false);
  }, [isDesktop]);

  const openPalette = useCallback(() => {
    setPaletteOpen(true);
    setNavOpen(false);
    // Opening is a new search, not a return to the last one.
    setQuery('');
  }, []);

  // ⌘K / Ctrl+K opens the palette from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openPalette();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPalette]);

  const toggleRail = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(LS_EXPANDED, next ? '1' : '0');
      } catch (e) {
        // Private browsing or blocked storage: the preference just does not persist.
      }
      return next;
    });
  };

  const toggleGroup = (id) => setOpenGroupId((prev) => (prev === id ? null : id));

  const { entityGroups, loadingEntities } = useEntitySearch({ query, enabled: paletteOpen });

  // Flattened destination list for ⌘K. These are local and match as you type; the record hits above
  // come from the server.
  const modules = useMemo(() => sections.flatMap((section) => section.groups.flatMap((group) => {
    if (!group.leaves || group.leaves.length === 0) {
      return [{ name: group.name, group: section.label, icon: group.icon, url: group.url }];
    }
    return group.leaves.map((leaf) => ({
      name: leaf.name, group: group.name, section: section.label, icon: group.icon, url: leaf.url,
    }));
  })), [sections]);

  const railProps = {
    sections,
    active,
    openGroupId,
    onToggleGroup: toggleGroup,
    onToggleRail: toggleRail,
    currentUser: props.currentUser,
    logoutLink: props.logoutLink,
    rootURL: props.rootURL,
    logoURL: props.logoURL,
    logoIconURL: props.logoIconURL,
    appName: 'Unimart Delivery',
  };

  const railContent = <Rail {...railProps} expanded={expanded} touch={false} />;

  return (
    <>
      {isMobile ? (
        <>
          <MobileTopBar
            rootURL={props.rootURL}
            logoURL={props.logoURL}
            appName="Unimart Delivery"
            active={active}
            onOpenNav={() => setNavOpen(true)}
            onOpenPalette={openPalette}
            searchLabel={SEARCH_PLACEHOLDER}
          />
          {/* The same rail the desktop shows, behind the header's menu button —
              the pattern Hub uses. Always expanded here, with touch-sized rows. */}
          <Drawer
            variant="temporary"
            open={navOpen}
            onClose={() => setNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { width: RAIL_WIDTH, border: 0, bgcolor: C.ink, overflow: 'hidden' } }}
          >
            {/* A navigation tap should leave the drawer behind it. */}
            <Box
              sx={{ height: '100%' }}
              onClick={(event) => { if (event.target.closest?.('a')) setNavOpen(false); }}
            >
              <Rail {...railProps} expanded touch collapsible={false} />
            </Box>
          </Drawer>
        </>
      ) : (
        <>
          <Drawer
            variant="permanent"
            sx={{
              width: railWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: railWidth, border: 0, bgcolor: C.ink, overflow: 'hidden',
                transition: 'width 200ms cubic-bezier(.2,0,0,1)',
              },
            }}
          >
            {railContent}
          </Drawer>

          <TopBar
            expanded={expanded}
            railWidth={railWidth}
            active={active}
            onToggleRail={toggleRail}
            onOpenPalette={openPalette}
            pageTrail={pageTrail}
            searchPlaceholder={SEARCH_PLACEHOLDER}
          />
        </>
      )}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        modules={modules}
        entityGroups={entityGroups}
        loadingEntities={loadingEntities}
        query={query}
        onQueryChange={setQuery}
        placeholder={SEARCH_PLACEHOLDER}
      />
    </>
  );
};

AppShell.propTypes = propTypes;
AppShell.defaultProps = defaultProps;

export default AppShell;
